import { Button } from '@creditornot/cb-button';
import { elevationCss } from '@creditornot/cb-ingredients';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import { checkIfSelectionCanBePublished, isNewSelection } from 'modules/shared-menu/utils';

import { useSelectionData } from '../../../hooks/useSelectionData';

type EditorStickyFooterProps = {
  onDiscardClick: () => void;
  onScheduleClick: () => void;
  onPublishClick: () => void;
};

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-inline: 60px;
`;

const DiscardChangesButton = styled(Button).attrs({
  variant: 'lightRed',
  size: 'small',
})``;

const PublishButton = styled(Button).attrs({
  variant: 'blue',
  size: 'small',
})``;

const SchedulePublishingButton = styled(PublishButton)``;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`;

const StickyFooter = styled.div<{ $isVisible: boolean }>`
  ${({ $isVisible }) => css`
    ${elevationCss(16)}
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px;
    display: flex;
    justify-content: flex-end;
    position: fixed;
    animation: ${$isVisible ? slideUp : slideDown} both cubic-bezier(0, 0.04, 0, 0.95);
    animation-duration: 400ms;
  `}
`;

export const EditorStickyFooter = ({ onDiscardClick, onPublishClick, onScheduleClick }: EditorStickyFooterProps) => {
  const { selection } = useSelectionData();
  const publishable = checkIfSelectionCanBePublished(selection);

  // Hide scheduling button for new selections that haven't been published yet
  //
  // It's possible that older selections have been scheduled before this UI limit was put in place
  // Show the button also in this case, to allow editing and/or removing the scheduling time
  const showSchedulingButton = !isNewSelection(selection) || !!selection.draft?.publish_at;

  return (
    <StickyFooter $isVisible={publishable}>
      <ButtonsRow>
        <DiscardChangesButton onClick={onDiscardClick}>
          <FormattedMessage id="shared-menu.delete-draft.button-label" />
        </DiscardChangesButton>
        {showSchedulingButton && (
          <SchedulePublishingButton disabled={!publishable} onClick={onScheduleClick}>
            {!!selection.draft?.publish_at ? (
              <FormattedMessage id="shared-menu.publish.scheduled.edit-label" />
            ) : (
              <FormattedMessage id="shared-menu.publish.scheduled.label" />
            )}
          </SchedulePublishingButton>
        )}
        <PublishButton disabled={!publishable} variant="blue" size="small" onClick={onPublishClick}>
          <FormattedMessage id="shared-menu.publish.now.label" />
        </PublishButton>
      </ButtonsRow>
    </StickyFooter>
  );
};
